import { useMemo, useState } from "react";
import { Input, Checkbox, Button, Autocomplete } from '@fullscreendigital/carrefour-dex-fe'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { getInitialValues, getValidationSchema } from './helpers'
import { useNotification, useSearchInput } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { CircularProgress, Grid, createFilterOptions } from '@mui/material'
import { makeCoreGetRequest, makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { useMutation, useQuery } from 'react-query'
import { AutocompleteOption } from 'components/volumetry/interfaces'
import { WarningModal } from "./WarningModal";

interface NewAdminDepositProps {
  isOpen: boolean
  closeModal: Function
  setShouldRefreshData: Function
  isEdit?: boolean
  editItem?: AdminHub
}

const filter = createFilterOptions()

const AdminHubForm: React.FC<NewAdminDepositProps> = props => {
  const { isOpen, closeModal, isEdit, editItem, setShouldRefreshData } = props
  const { showNotification } = useNotification()

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false)
  const [tempUsers, setTempUsers] = useState<any[]>([]);
  const [existingHubName, setExistingHubName] = useState('');

  const openConfirmationModal = (): void => setIsConfirmationModalOpen(true)
  const closeConfirmationModal = (): void => setIsConfirmationModalOpen(false)

  const mutateHub = async values => {
    editItem
      ? await makeCorePutRequest(`/shop-reception/hubs/${editItem.id}`, values)
      : await makeCorePostRequest('/shop-reception/hubs', values)
  }

  const { isLoading, mutate } = useMutation(mutateHub, {
    onSuccess: () => {
      setShouldRefreshData(true)
      closeModal()
      formik.resetForm()
      showNotification({
        message: `Hubul a fost ${editItem ? 'modificat' : 'creat'} cu succes.`,
        type: 'success'
      })
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare la ${
          editItem ? 'modificarea' : 'crearea'
        } hubului. ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const { data: hubTypes, isLoading: isHubTypesLoading } = useQuery(
    ['hub-types'],
    async () => {
      const result = await makeCoreGetRequest('/shop-reception/hubs/types')

      const resultOptions = result.map(
        (item: any): AutocompleteOption => ({
          label: item,
          value: item
        })
      )

      return resultOptions || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare la obtinerea huburilor. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: assignedUsers, isLoading: isAssignedUsersLoading } = useQuery(
    ['assigned-users', isOpen && isEdit],
    async () => {
      const result = await makeCoreGetRequest(`/shop-reception/hubs/${editItem.id}/users`)

      return result || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare la obtinerea utilizatorilor. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      },
      enabled: isOpen && typeof editItem !== 'undefined'
    }
  )

  const initialValues = useMemo(
    () => getInitialValues(editItem, hubTypes, assignedUsers),
    [editItem, hubTypes, assignedUsers]
  )
  const validationSchema = useMemo(() => getValidationSchema(), [editItem])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      const payload = {
        ...values,
        type: values.type.value,
        packagingResponsibleEmails: values.packagingResponsibleEmails.split(';')
      }

      mutate(payload)
    }
  })

  const selectProductSearch = useSearchInput(
    'name',
    'deposit-responsible',
    '/shop-reception/hubs/responsible'
  )

  const handleUserSelection = async (event, value) => {
    const previousUsers = formik.values.users || [];
    const currentUsers = value.map(val => (val.inputValue ? val.inputValue : val));

    if (currentUsers.length > previousUsers.length) {
      const addedUser = currentUsers.find(
        user => !previousUsers.some(prevUser => (prevUser.username || prevUser) === (user.username || user))
      );

      if (addedUser) {
        try {
          const username = addedUser.username || addedUser;

          const response = await makeCoreGetRequest(`/shop-reception/search-users/hub-name-for-user/${username}`);

          if (response) {
            setExistingHubName(response as string);
            setTempUsers(currentUsers);
            openConfirmationModal();
          } else {
            formik.setFieldValue('users', currentUsers);
          }
        } catch (error) {
          showNotification({
            message: `A apărut o eroare la verificarea utilizatorului. ${formatErrorMessage(error)}`,
            type: 'error',
          });
          formik.setFieldValue('users', previousUsers);
        }
      } else {
        formik.setFieldValue('users', currentUsers);
      }
    } else {
      formik.setFieldValue('users', currentUsers);
    }
  };

  const confirm = () => {
    formik.setFieldValue('users', tempUsers);
    closeConfirmationModal();
  };

  return (
    <Modal
      isModalOpen={isOpen}
      closeModal={closeModal}
      modalTitle={isEdit ? 'Editează hub' : 'Adaugă hub'}
      modalWidth="50%"
    >
      <Grid container spacing={2}>
        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.code}
            onChange={event =>
              formik.setFieldValue('code', (event.target as HTMLInputElement).value)
            }
            label="Cod HUB"
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.code}
            helperText={formik.errors.code as string}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.name}
            onChange={event =>
              formik.setFieldValue('name', (event.target as HTMLInputElement).value)
            }
            label="Nume"
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.name}
            helperText={formik.errors.name as string}
            required
          />
        </Grid>

        <Grid xs={12} item>
          {isHubTypesLoading ? (
            <div style={{ display: 'flex' }}>
              <CircularProgress color="primary" disableShrink />
            </div>
          ) : (
            <Autocomplete
              variant="outlined"
              label="Tip HUB"
              disableClearable
              required
              onChange={(event, option) => formik.setFieldValue('type', option)}
              value={formik.values.type}
              options={hubTypes || []}
              error={!!formik.errors.type}
              helperText={formik.errors.type as string}
            />
          )}
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.packagingResponsibleEmails}
            onChange={event =>
              formik.setFieldValue(
                'packagingResponsibleEmails',
                (event.target as HTMLInputElement).value
              )
            }
            label="Adrese de email responsabil ambalaje"
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.packagingResponsibleEmails}
            helperText={formik.errors.packagingResponsibleEmails as string}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.address}
            onChange={event =>
              formik.setFieldValue('address', (event.target as HTMLInputElement).value)
            }
            label="Adresă"
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.address}
            helperText={formik.errors.address as string}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Checkbox
            checked={formik.values.active}
            defaultChecked={formik.values.active}
            onChange={() => formik.setFieldValue('active', !formik.values.active)}
            label="Activ"
            color={!!formik.errors.active ? 'primary' : 'secondary'}
          />
        </Grid>

        <Grid xs={12} item>
          <Autocomplete
            {...selectProductSearch}
            variant="outlined"
            label="Utilizatori asignaţi pe hub"
            autoComplete={false}
            multiple
            loading={isAssignedUsersLoading}
            value={formik.values.users || null}
            error={!!formik.errors.users}
            helperText={formik.errors.users as string}
            onChange={handleUserSelection}
            filterOptions={(options, params) => {
              const filtered = filter(options, params)

              if (params.inputValue !== '') {
                filtered.push({
                  inputValue: params.inputValue,
                  label: `Adauga utilizator "${params.inputValue}"`
                })
              }

              return filtered
            }}
          />
        </Grid>
        <WarningModal
          isModalOpen={isConfirmationModalOpen}
          closeModal={closeConfirmationModal}
          confirm={confirm}
          hubName={existingHubName}
        />

        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Anuleaza" onClick={() => closeModal()} />
          <Button
            variant="contained"
            label={'Salveaza'}
            onClick={() => formik.handleSubmit()}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default AdminHubForm
